/* eslint-disable no-async-promise-executor */
import { canvasRequest } from '$/api/canvasRquest';
import APP from '$/api/api';
import { useLanguageStore } from '$/store/languageStore';
import { useOfficialStore } from '$/store/useOfficialStore';

/** 在场用户状态：editing=持锁编辑，viewing=仅打开预览 */
export type TeamCanvasLockUserStatus = 'editing' | 'viewing';

/** 画布锁在场用户 */
export interface TeamCanvasLockUser {
  uid: number;
  screen_name: string;
  avatar: string;
  status: TeamCanvasLockUserStatus;
}

/** acquire / release / renew 统一快照 */
export interface TeamCanvasLockSnapshot {
  project_id?: string;
  room_id?: string;
  /** 是否有人持锁 */
  locked: boolean;
  /** 当前页签是否持锁人 */
  is_holder: boolean;
  /** 当前是否可调用 acquire（锁空闲） */
  can_acquire: boolean;
  /** 本人已在其他页签持锁 */
  tab_conflict: boolean;
  /** 持锁人 UID；无人则为 0 */
  holder_uid: number;
  /** 编辑锁 TTL 秒 */
  ttl?: number;
  /** 在场用户（个人版通常仅本人；团队版含协作成员） */
  users: TeamCanvasLockUser[];
}

export interface TeamCanvasLockRequestParams {
  project_id: string;
  room_id?: string;
  tab_id: string;
}

interface CanvasLockEndpoints {
  acquire: string;
  release: string;
  renew: string;
}

/**
 * 判断当前是否处于团队态。
 */
function isTeamEditionActive() {
  return Boolean(useOfficialStore.getState().clientOId);
}

/**
 * 按团队 / 个人态选择画布锁接口路径。
 * 团队：/team/canvas/lock/*；个人：/v3/canvas/lock/*（仅页签互斥）。
 */
function getCanvasLockEndpoints(): CanvasLockEndpoints {
  if (isTeamEditionActive()) {
    return {
      acquire: APP.roboneo.api.teamCanvasLockAcquire,
      release: APP.roboneo.api.teamCanvasLockRelease,
      renew: APP.roboneo.api.teamCanvasLockRenew,
    };
  }
  return {
    acquire: APP.roboneo.api.canvasLockAcquireV3,
    release: APP.roboneo.api.canvasLockReleaseV3,
    renew: APP.roboneo.api.canvasLockRenewV3,
  };
}

/**
 * 规范化锁接口返回，补齐默认值。
 */
function normalizeLockSnapshot(data: Partial<TeamCanvasLockSnapshot> | null | undefined): TeamCanvasLockSnapshot {
  return {
    project_id: data?.project_id ? String(data.project_id) : undefined,
    room_id: data?.room_id ? String(data.room_id) : undefined,
    locked: Boolean(data?.locked),
    is_holder: Boolean(data?.is_holder),
    can_acquire: Boolean(data?.can_acquire),
    tab_conflict: Boolean(data?.tab_conflict),
    holder_uid: Number(data?.holder_uid) || 0,
    ttl: typeof data?.ttl === 'number' ? data.ttl : undefined,
    users: Array.isArray(data?.users) ? data.users : [],
  };
}

/**
 * 发起画布锁 POST，成功返回规范化快照。
 */
async function requestLock(
  url: string,
  params: TeamCanvasLockRequestParams,
): Promise<TeamCanvasLockSnapshot> {
  return new Promise<TeamCanvasLockSnapshot>(async (resolve, reject) => {
    try {
      const response = await canvasRequest.request({
        url,
        method: 'POST',
        data: {
          project_id: params.project_id,
          room_id: params.room_id,
          tab_id: params.tab_id,
        },
      });
      if (response.status === 200 && response.data.code === 0) {
        resolve(normalizeLockSnapshot(response.data.data));
        return;
      }
      reject(response);
    } catch (error) {
      reject(error);
    }
  });
}

/**
 * 获取画布编辑锁：团队 /team/canvas/lock/acquire，个人 /v3/canvas/lock/acquire。
 */
export async function acquireCanvasLock(params: TeamCanvasLockRequestParams) {
  return requestLock(getCanvasLockEndpoints().acquire, params);
}

/**
 * 释放画布编辑锁：团队 /team/canvas/lock/release，个人 /v3/canvas/lock/release。
 */
export async function releaseCanvasLock(params: TeamCanvasLockRequestParams) {
  return requestLock(getCanvasLockEndpoints().release, params);
}

/**
 * 画布锁心跳 / 查状态：团队 /team/canvas/lock/renew，个人 /v3/canvas/lock/renew。
 */
export async function renewCanvasLock(params: TeamCanvasLockRequestParams) {
  return requestLock(getCanvasLockEndpoints().renew, params);
}

/**
 * 页面卸载时尽力释放编辑锁（fetch keepalive，避免异步 POST 被取消）。
 */
export function releaseCanvasLockKeepalive(params: TeamCanvasLockRequestParams) {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    const {
      clientId,
      clientOId,
      clientOGroupId,
      gid,
      currentCountryCode,
      userInfo,
    } = useOfficialStore.getState();
    const { languageCode } = useLanguageStore.getState().languageStore;
    const baseURL = APP.roboneo.baseCanvasURL();
    const path = getCanvasLockEndpoints().release;
    const url = `${baseURL.replace(/\/$/, '')}${path.startsWith('/') ? path : `/${path}`}`;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'access-token': userInfo?.token || '',
      'client-id': String(clientId || ''),
    };
    if (clientOId != null) {
      headers['Client-OID'] = String(clientOId);
    }
    if (clientOGroupId) {
      headers['Client-OGroupId'] = String(clientOGroupId);
    }
    const body = JSON.stringify({
      gnum: gid,
      client_id: clientId,
      client_language: languageCode,
      country_code: currentCountryCode,
      project_id: params.project_id,
      room_id: params.room_id,
      tab_id: params.tab_id,
    });
    fetch(url, {
      method: 'POST',
      headers,
      body,
      keepalive: true,
      credentials: 'include',
    }).catch(() => undefined);
  } catch (error) {
    console.error('[canvasLock] keepalive release failed', error);
  }
}

/** @deprecated 请使用 acquireCanvasLock；保留别名兼容既有调用 */
export async function acquireTeamCanvasLock(params: TeamCanvasLockRequestParams) {
  return acquireCanvasLock(params);
}

/** @deprecated 请使用 releaseCanvasLock；保留别名兼容既有调用 */
export async function releaseTeamCanvasLock(params: TeamCanvasLockRequestParams) {
  return releaseCanvasLock(params);
}

/** @deprecated 请使用 releaseCanvasLockKeepalive；保留别名兼容既有调用 */
export function releaseTeamCanvasLockKeepalive(params: TeamCanvasLockRequestParams) {
  releaseCanvasLockKeepalive(params);
}

/** @deprecated 请使用 renewCanvasLock；保留别名兼容既有调用 */
export async function renewTeamCanvasLock(params: TeamCanvasLockRequestParams) {
  return renewCanvasLock(params);
}
